<template>
	<BaseColumn
		v-if="tagGroup"
		:size="1"
		:title="tagGroup.name"
		class="tag-group-preview"
		:disabled="disabled"
		:disabled-message="disabledMessage"
	>
		<template #body>
			<div class="section">
				<div class="section-header">
					<h4>{{ transUcfirst('commons.details') }}</h4>
				</div>

				<div class="section-content">
					<div class="visibility">
						<div class="tag-type-group">
							<FormField
								:label="transUcfirst('commons.visibility')"
							>
								<VisibilityDropdown
									:value="visibility"
									:disabled="!isFormEnabled"
									@input="onVisibilityChanged"
								/>
							</FormField>
						</div>
					</div>
				</div>
			</div>
			<div class="section section-statuses">
				<TagGroupSections
					:title="sectionGroups.event.title"
					:sections="sectionGroups.event.sectionStatuses"
					:editable="isFormEnabled"
					@section-toggle="onSectionToggled"
				/>
				<TagGroupSections
					:title="sectionGroups.participant.title"
					:sections="sectionGroups.participant.sectionStatuses"
					:editable="isFormEnabled"
					@section-toggle="onSectionToggled"
				/>
			</div>
		</template>
		<template #footer>
			<div
				v-if="hasChanges"
				class="button-group"
			>
				<DiscardButton
					@click.native="initForm"
				/>
				<button
					class="button button--primary"
					data-qa="save-tag-button"
					:disabled="isSaving"
					@click="save"
				>
					{{ transUcfirst('commons.save') }}
				</button>
			</div>
			<div
				v-else
				class="button-group"
			>
				<button
					class="button button--secondary button--icon"
					@click="onEditClick"
				>
					<Icon
						icon-name="pen"
						size="lg"
					/>
					<span>{{ editBtnLabel }}</span>
				</button>
			</div>
		</template>
	</BaseColumn>
</template>

<script>
import BaseColumn from '@videotools/js/one/components/Settings/BaseColumn';
import Helpers from '@legacy/js/helpers';
import TagGroupSections from '@videotools/js/one/components/TagGroups/TagGroupSections';
import FormField from '@videotools/js/one/components/Form/FormField';
import VisibilityDropdown from '@videotools/js/one/components/TagGroups/VisibilityDropdown';
import { mapActions, mapGetters } from 'vuex';
import {
	SECTION_CUSTOM_EVENT,
	SECTION_CUSTOM_PARTICIPANT,
	SECTION_COMPETITION,
	SECTION_SCOREBOARD,
} from '@videotools/js/api/entities/TagGroup';
import DiscardButton from '@components/buttons/DiscardButton';

const DEBOUNCE_DELAY = 1000; // in ms

export default {
	name:       'TagGroupPreview',
	components: { DiscardButton, VisibilityDropdown, FormField, TagGroupSections, BaseColumn },
	props:      {
		tagGroup: {
			type:    Object,
			default: null,
		},
		disabled: {
			type: Boolean,
		},
		disabledMessage: {
			type: String,
		},
	},
	data() {
		return {
			visibility:      null,
			enabledSections: [],
			debounceTimeout: null,
			isSaving:        false,
			hasChanges:      false,
		};
	},
	computed: {
		...mapGetters({
			isPlayerCompetition:     'event/isPlayerCompetition',
			tagGroupSectionStatuses: 'tagGroups/tagGroupSectionStatuses',
			eventSections:           'tagGroups/eventSections',
			participantSections:     'tagGroups/participantSections',
		}),
		sectionTitle() {
			const helpers = new Helpers();
			return !this.tagGroup?.visibility ? '' : helpers.ucfirst(this.tagGroup.visibility);
		},
		sectionStatuses() {
			return Object.keys(this.tagGroupSectionStatuses(this.tagGroup)).map(s => {
				let editable = true;
				if (
					(SECTION_CUSTOM_PARTICIPANT === s && !this.tagGroup.customParticipantButtons.length) ||
				  (SECTION_CUSTOM_EVENT === s && !this.tagGroup.customEventButtons.length)
				) {
					editable = false;
				}
				return {
					name:    s,
					enabled: this.enabledSections.includes(s),
					editable,
				};
			});
		},
		isFormEnabled() {
			return !this.tagGroup.isSystemDefault && !this.isSaving;
		},
		editBtnLabel() {
			return this.transUcfirst('commons.type_edit', {
				type: this.transChoiceUcFirst('tag_group.tag_group', 1),
			});
		},
		sectionGroups() {
			const eventSections = this.sectionStatuses.filter(sectionStatus => this.eventSections.includes(sectionStatus.name));
			eventSections.sort((a, b) => {
				return this.sectionOrderEvent[a.name] < this.sectionOrderEvent[b.name]	? -1 : 1;
			});

			const participantSections = this.sectionStatuses.filter(sectionStatus => this.participantSections.includes(sectionStatus.name));
			participantSections.sort((a, b) => {
				return this.sectionOrderParticipant[a.name] < this.sectionOrderParticipant[b.name]	? -1 : 1;
			});

			return {
				event: {
					title:           this.transChoiceUcFirst('commons.event', 1),
					sectionStatuses: eventSections,
				},
				participant: {
					title:           this.isPlayerCompetition ? this.transChoiceUcFirst('commons.athlete', 1) : this.transChoiceUcFirst('commons.team', 1),
					sectionStatuses: participantSections,
				},
			};
		},
		sectionOrderParticipant() {
			return this.participantSections.reduce((acc, section) => {
				acc[section] = this.participantSections.indexOf(section);
				return acc;
			}, {});
		},
		sectionOrderEvent() {
			return this.eventSections.reduce((acc, section) => {
				acc[section] = this.eventSections.indexOf(section);
				return acc;
			}, {});
		},
	},
	created() {
		this.initForm();
	},
	updated() {
		this.initForm();
	},
	methods: {
		...mapActions({
			update: 'tagGroups/update',
		}),
		initForm() {
			const sectionStatuses = this.tagGroupSectionStatuses(this.tagGroup);
			this.enabledSections = Object.keys(sectionStatuses)
				.filter(sectionName => sectionStatuses[sectionName] === true);
			this.visibility = this.tagGroup.visibility;
			this.hasChanges = false;
		},
		save() {
			this.saveForm({
				tagGroupId: this.tagGroup.id,
				sections:   this.enabledSections,
				visibility: this.visibility,
			});
		},
		toggleSection(section, status) {
			this.enabledSections = status
				? [...this.enabledSections, section]
				: this.enabledSections.filter(s => s !== section);
		},
		onSectionToggled({ section, enabled }) {
			this.toggleSection(section, enabled);
			if (section === SECTION_SCOREBOARD && enabled) {
				// also enable match section when scoreboard is enabled
				this.toggleSection(SECTION_COMPETITION, true);
			}
			if (section === SECTION_COMPETITION && !enabled) {
				// also disable scoreboard is match is disabled
				this.toggleSection(SECTION_SCOREBOARD, false);
			}

			this.hasChanges = true;
		},
		onVisibilityChanged(visibility) {
			this.visibility = visibility;
			this.hasChanges = true;
		},
		onEditClick() {
			this.$emit('edit-tag-group', this.tagGroup);
		},
		saveDebounced({ tagGroupId, sections, visibility }) {
			if (this.debounceTimeout) {
				clearTimeout(this.debounceTimeout);
				this.debounceTimeout = null;
			}

			this.debounceTimeout = setTimeout(() => {
				this.saveForm({ tagGroupId, sections, visibility });
			}, DEBOUNCE_DELAY);
		},
		async saveForm({ tagGroupId, sections, visibility }) {
			this.isSaving = true;

			try {
				await this.update({
					tagGroupId:   tagGroupId,
					tagGroupData: {
						sections,
						visibility,
					},
				});
			} finally {
				this.isSaving = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/sass/fonts';

.tag-group-preview {
	background: var(--color-grey-900);
	color: var(--color-soft-white);
}

.section-title {
	@include text-md-2;
}

.section-content {
	display: flex;

	> * {
		flex: 0 1 50%;
	}
}

.visibility {
	display: flex;
	align-items: center;

	label {
		margin-right: 8px;
	}

	.icon--block {
		position: relative;
		bottom: 2px;
	}

	&::v-deep {
		.form-field--wrapper {
			width: 200px;
		}

		.form-field--input-wrapper {
			width: 100%;
		}
	}
}

.section-statuses {
	margin-top: 40px;

	.tag-group-sections + .tag-group-sections {
		margin-top: 32px;
	}
}

.form-field--wrapper {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

</style>
